import { usePost, useFetch } from '@/core/hooks/useFetch';
import type { ApplyFormModel } from '@/apps/public/components/modals/apply/apply.model';
import type { ApplicationFields } from '@/core/collections/job-boards/job-board.interface';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';
import { POSITIONS_ENDPOINTS } from './positions.endpoints';

function serialize(data: ApplyFormModel, applicationFields: ApplicationFields): FormData {
   // Filter rules:
   // - first_name, last_name and phone are always required
   // - hidden (null in applicationFields): filter out
   // - optional (false) with no value: filter out
   // - required (true) or optional with value: keep
   const filteredData: Record<string, any> = {};
   const alwaysRequired = ['first_name', 'last_name', 'phone_number'];

   for (const [key, value] of Object.entries(data)) {
      // Name and phone are always required - always include
      if (alwaysRequired.includes(key)) {
         filteredData[key] = value;
         continue;
      }

      const fieldConfig = applicationFields[key as keyof ApplicationFields];

      // Hidden field - skip
      if (fieldConfig === null) continue;

      // Optional field (false) with no value - skip
      if (fieldConfig === false && (value === null || value === '' || value === undefined)) continue;

      // Required (true), optional with value, or field not in applicationFields - include
      filteredData[key] = value;
   }

   // FormData for file upload binary
   const formData = new FormData();
   for (const [key, value] of Object.entries(filteredData)) {
      if (value instanceof File || value instanceof Blob) {
         formData.append(key, value);
      } else if (value !== null && typeof value === 'object') {
         // object-to-formdata
         // { address: { street: 'Wolvenstraat', city: 'Kortrijk' } }
         // is transformed to:
         // address[street]: 'Wolvenstraat'
         // address[city]  : 'Kortrijk'
         for (const [nestedKey, nestedValue] of Object.entries(value)) {
            formData.append(`${key}[${nestedKey}]`, String(nestedValue));
         }
      } else {
         formData.append(key, value);
      }
   }

   return formData;
}

export function applyToPosition(positionId: string | number, data: ApplyFormModel, applicationFields: ApplicationFields) {
   const formData = serialize(data, applicationFields);

   const { activeLanguage } = useJobBoardStore();
   formData.append('language_id', String(activeLanguage?.id ?? 1));

   return usePost(POSITIONS_ENDPOINTS.APPLICATION(positionId), { data: formData });
}

export function getPositionDetail(positionId: string | number): any {
   return useFetch(POSITIONS_ENDPOINTS.DETAIL(positionId));
}
